import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Checkbox } from '../../../components/ui/checkbox.js';
import { Input } from '../../../components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.js';
import { Slider } from '../../../components/ui/slider.js';
import { TagChip } from '../../scenario/components/TagChip.js';

/**
 * Advanced filter sidebar for the Presentation list. Mirrors the
 * legacy `wf-audit/.../presentation/components/presentationFilter/Provider.tsx`
 * drawer:
 *   - Scenario text search (mirror — the page's main search input
 *     stays the primary entry; this field is a redundant copy that
 *     legacy operators reach for inside the drawer).
 *   - Tags multi-select chip strip.
 *   - Modules range slider (`[min, max]` with `min: 1`, `max: 90`).
 *   - Has Notifications checkbox.
 *   - Applications multi-select.
 *   - Capabilities free-text search (over scenario description /
 *     AI-summary blob).
 *   - Last Run date filter + Before/On/After tri-state.
 *
 * State is hoisted to the parent (`PresentationListPage`); this
 * component owns no draft buffer — every change applies immediately
 * (matches legacy "live filters" UX, no Apply button). `Reset`
 * clears every field; `Close` dismisses the sheet without changing
 * filter values.
 */
export interface PresentationFilters {
  tagIds: ReadonlyArray<number>;
  modulesMin: number;
  modulesMax: number;
  hasNotifications: boolean;
  applicationTypes: ReadonlyArray<string>;
  capabilities: string;
  lastRunDate: string | null;
  lastRunComparator: 'before' | 'on' | 'after';
}

export const EMPTY_FILTERS: PresentationFilters = {
  tagIds: [],
  modulesMin: 1,
  modulesMax: 200,
  hasNotifications: false,
  applicationTypes: [],
  capabilities: '',
  lastRunDate: null,
  lastRunComparator: 'on',
};

export interface PresentationDataFiltersSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  filters: PresentationFilters;
  onChange: (next: PresentationFilters) => void;
  /** All tags in the license, used for the chip strip. */
  allTags: ReadonlyArray<{
    id: number;
    title: string;
    color?: string | null;
    icon?: string | null;
  }>;
  /** Application slugs that appear in the unfiltered scenario list,
   *  used to populate the Applications multi-select. */
  allApplicationTypes: ReadonlyArray<string>;
  /** Max module count across the scenario list — drives the slider
   *  upper bound dynamically (legacy hardcoded `90`; we follow the
   *  data so a tenant with a 300-module scenario doesn't lose
   *  reach). */
  maxModules: number;
}

export function PresentationDataFiltersSheet({
  open,
  onOpenChange,
  filters,
  onChange,
  allTags,
  allApplicationTypes,
  maxModules,
}: PresentationDataFiltersSheetProps): JSX.Element {
  // Mirror the parent filter state into a local draft so the slider
  // doesn't re-render the entire scenario list on every drag pixel.
  // Flush on commit (`onValueCommit`).
  const [modulesDraft, setModulesDraft] = useState<[number, number]>([
    filters.modulesMin,
    Math.min(filters.modulesMax, Math.max(maxModules, filters.modulesMax)),
  ]);
  useEffect(() => {
    setModulesDraft([filters.modulesMin, filters.modulesMax]);
  }, [filters.modulesMin, filters.modulesMax]);

  const upperBound = Math.max(maxModules, 90, filters.modulesMax);

  const sortedApps = useMemo(
    () => [...allApplicationTypes].sort((a, b) => a.localeCompare(b)),
    [allApplicationTypes],
  );

  function patch(next: Partial<PresentationFilters>): void {
    onChange({ ...filters, ...next });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-full overflow-y-auto border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground sm:max-w-md"
      >
        <SheetHeader>
          <SheetTitle className="text-foreground">Data Filters</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          <section className="space-y-1.5">
            <label
              htmlFor="filter-tags"
              className="block text-xs font-medium uppercase tracking-wider text-foreground/60"
            >
              Tags
            </label>
            {allTags.length === 0 ? (
              <p className="text-xs text-foreground/45">No tags defined for this license.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {allTags.map((t) => {
                  const active = filters.tagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() =>
                        patch({
                          tagIds: active
                            ? filters.tagIds.filter((id) => id !== t.id)
                            : [...filters.tagIds, t.id],
                        })
                      }
                      className={
                        active
                          ? 'rounded-full ring-2 ring-[color:var(--brand-yellow)] ring-offset-2 ring-offset-[color:var(--surface-overlay)]'
                          : 'rounded-full opacity-70 transition-opacity hover:opacity-100'
                      }
                    >
                      <TagChip tag={t} size="sm" />
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-foreground/60">
                Modules
              </span>
              <span className="text-xs text-foreground/55">
                {modulesDraft[0]} – {modulesDraft[1]}
              </span>
            </div>
            <Slider
              min={1}
              max={upperBound}
              step={1}
              value={modulesDraft}
              onValueChange={(v) => setModulesDraft([v[0] ?? 1, v[1] ?? upperBound])}
              onValueCommit={(v) =>
                patch({
                  modulesMin: v[0] ?? 1,
                  modulesMax: v[1] ?? upperBound,
                })
              }
              className="py-1"
            />
            <p className="text-[11px] text-foreground/45">
              Selected range: {modulesDraft[0]} – {modulesDraft[1]}
            </p>
          </section>

          <section className="flex items-center gap-2">
            <Checkbox
              id="filter-has-notifications"
              checked={filters.hasNotifications}
              onCheckedChange={(c) => patch({ hasNotifications: c === true })}
            />
            <label
              htmlFor="filter-has-notifications"
              className="cursor-pointer text-sm text-foreground/90"
            >
              Has Notifications
            </label>
          </section>

          <section className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-foreground/60">
              Applications
            </label>
            {sortedApps.length === 0 ? (
              <p className="text-xs text-foreground/45">No applications detected.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {sortedApps.map((slug) => {
                  const active = filters.applicationTypes.includes(slug);
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() =>
                        patch({
                          applicationTypes: active
                            ? filters.applicationTypes.filter((s) => s !== slug)
                            : [...filters.applicationTypes, slug],
                        })
                      }
                      className={
                        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs transition-colors ' +
                        (active
                          ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                          : 'border-foreground/15 bg-transparent text-foreground/70 hover:border-foreground/40 hover:text-foreground')
                      }
                    >
                      {slug}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="space-y-1.5">
            <label
              htmlFor="filter-capabilities"
              className="block text-xs font-medium uppercase tracking-wider text-foreground/60"
            >
              Capabilities
            </label>
            <textarea
              id="filter-capabilities"
              value={filters.capabilities}
              onChange={(e) => patch({ capabilities: e.target.value })}
              rows={2}
              placeholder="Search by scenario capabilities/description…"
              className="w-full rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/40"
            />
          </section>

          <section className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-foreground/60">
              Last Run
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.lastRunDate ?? ''}
                onChange={(e) => patch({ lastRunDate: e.target.value || null })}
                className="flex-1 border-foreground/15 bg-foreground/5"
              />
            </div>
            <div className="inline-flex overflow-hidden rounded-md border border-foreground/15">
              {(['before', 'on', 'after'] as const).map((cmp) => {
                const active = filters.lastRunComparator === cmp;
                return (
                  <button
                    key={cmp}
                    type="button"
                    onClick={() => patch({ lastRunComparator: cmp })}
                    className={
                      'px-3 py-1 text-xs capitalize transition-colors ' +
                      (active
                        ? 'bg-foreground/15 text-foreground'
                        : 'text-foreground/65 hover:bg-foreground/5 hover:text-foreground')
                    }
                  >
                    {cmp}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <SheetFooter className="mt-6 flex flex-row items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          >
            Reset
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
          >
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
